import { useMemo, useState } from 'react';
import { Stack, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import Paper from '@mui/material/Paper';
import CasesListTable from './cases-list-table';

/**
 * UnallocatedCasesList component that displays cases with language issues.
 */
const VIEW_LANGUAGE = 'language';
const VIEW_LIMIT = 'limit';

/**
 * UnallocatedCasesList component that displays cases with language issues or allocation limits exceeded.
 */
const UnallocatedCasesList = ({
  onRemoveCaseSelection,
  languageIssuesCases,
  limitReachedCases, // Added prop for limit exceeded cases
}) => {
  const [currentView, setCurrentView] = useState(VIEW_LANGUAGE); // State for toggle

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setCurrentView(newView);
    }
  };

  const languageIssuesCasesMap = useMemo(() => {
    if (!languageIssuesCases || languageIssuesCases.length === 0) {
      return new Map();
    }
    // Assuming languageIssuesCases is an array of case objects with _id
    return new Map(languageIssuesCases.map((issueCase) => [issueCase._id, issueCase]));
  }, [languageIssuesCases]);

  const limitReachedCasesMap = useMemo(() => {
    if (!limitReachedCases || limitReachedCases.length === 0) {
      return new Map();
    }
    // Assuming limitReachedCases is an array of case objects with _id
    return new Map(limitReachedCases.map((limitCase) => [limitCase._id, limitCase]));
  }, [limitReachedCases]);

  // Don't render the component if both lists are empty
  if (languageIssuesCasesMap.size === 0 && limitReachedCasesMap.size === 0) {
    return null;
  }

  const displayData = currentView === VIEW_LANGUAGE ? languageIssuesCasesMap : limitReachedCasesMap;
  const displayTitle = currentView === VIEW_LANGUAGE ? 'Language Issues Cases' : 'Limit Reached Cases';

  return (
    <Stack spacing={2}>
      <Stack component={Paper} sx={{ p: 1 }} direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant='body1' sx={{ fontWeight: 'bold' }}>
          {displayTitle} ({displayData.size})
        </Typography>
        <ToggleButtonGroup
          color="primary"
          value={currentView}
          exclusive
          onChange={handleViewChange}
          aria-label="Case type view"
          size="small"
        >
          <ToggleButton value={VIEW_LANGUAGE} aria-label="language issues" disabled={languageIssuesCasesMap.size === 0}>
            Language ({languageIssuesCasesMap.size})
          </ToggleButton>
          <ToggleButton value={VIEW_LIMIT} aria-label="limit exceeded" disabled={limitReachedCasesMap.size === 0}>
            Limit ({limitReachedCasesMap.size})
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <CasesListTable
        onRemoveCase={onRemoveCaseSelection}
        data={displayData}
      />
    </Stack>
  );
};

export default UnallocatedCasesList;
